/**
 * Node id helpers for the workflow canvas.
 *
 * A node's id is derived from its title, and `{{steps.<id>.…}}` references in
 * other nodes' prompts point at it — so making an id, de-duplicating an id and
 * repointing references are the same three operations wherever nodes are
 * created. They live here rather than inside the canvas page because
 * lib/workflow-clipboard.ts needs the identical rules when pasting.
 */

/**
 * Turn a node title into a URL-safe id slug: lowercase, non-alphanumeric runs
 * collapse to a single hyphen, ends trimmed. An empty title falls back to the
 * node type so an id is always produced ("Send Welcome Email" → send-welcome-email).
 */
export function slugifyLabel(label: unknown, fallbackType: string): string {
  const slug = (typeof label === "string" ? label : "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || fallbackType;
}

/**
 * First free id of the form `base`, `base-2`, `base-3`… given the ids already
 * taken by other nodes. Two nodes titled the same stay unique without
 * surprising the user (two "Send Email" nodes → send-email, send-email-2).
 */
export function uniqueNodeId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * Rewrite every `{{steps.<oldId>.…}}` variable reference to point at <newId>,
 * walking strings, arrays and objects. The trailing dot anchors the match to a
 * whole id, so renaming `agent` never touches `agent-2`'s references.
 */
export function rewriteStepRefs(
  value: unknown,
  oldId: string,
  newId: string,
): unknown {
  if (typeof value === "string") {
    return value.split(`steps.${oldId}.`).join(`steps.${newId}.`);
  }
  if (Array.isArray(value)) {
    return value.map((v) => rewriteStepRefs(v, oldId, newId));
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = rewriteStepRefs(v, oldId, newId);
    }
    return out;
  }
  return value;
}

/**
 * Every `{{steps.<id>.…}}` id referenced anywhere inside a node's data.
 * Used to spot references that point at a node which was not copied.
 */
export function collectStepRefs(value: unknown, into: Set<string>): Set<string> {
  if (typeof value === "string") {
    for (const match of value.matchAll(/steps\.([A-Za-z0-9_-]+)\./g)) {
      into.add(match[1] as string);
    }
    return into;
  }
  if (Array.isArray(value)) {
    for (const v of value) collectStepRefs(v, into);
    return into;
  }
  if (value && typeof value === "object") {
    for (const v of Object.values(value as Record<string, unknown>)) {
      collectStepRefs(v, into);
    }
  }
  return into;
}
